import { useEffect } from 'react';
import { AlertTriangle, ArrowRight, Check, ShoppingCart } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useLocalizedNavigate } from '@/components/i18n';
import { getUpgradeUrl } from '@/lib/upgrade';
import { trackEvent } from '@/lib/posthog';
import { upsellEvents } from '@/lib/analytics/events/upsell';
import { paywallEvents, type SubscriptionTier } from '@/lib/analytics/events/paywall';
import { useAILimits, type AIFeatureType } from '@/hooks/useAILimits';
import { useAuth } from '@/contexts/AuthContext';
import { getPersonalizedSavings } from '@/lib/personalizedSavings';

interface UsageGateModalProps {
  feature: AIFeatureType;
  used: number;
  limit: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const CORE_BENEFITS = [
  '5 strategy regenerations',
  '10 document uploads',
  '100 Sage AI messages',
  '20 support calculations',
  '5 trade proposals',
  '5 legal document exports',
];

export function UsageGateModal({
  feature,
  used,
  limit,
  open,
  onOpenChange,
}: UsageGateModalProps) {
  const navigate = useLocalizedNavigate();
  const { profile } = useAuth();
  const { getFeatureName, getPackForFeature, isPaid, purchaseResourcePack, isPurchasingResourcePack, tier } = useAILimits();

  const featureName = getFeatureName(feature);
  const pack = getPackForFeature(feature);

  // Canonical hub event: the user hit a hard usage limit (this modal only
  // renders when a limit is reached). Fires once per open transition.
  useEffect(() => {
    if (!open) return;
    const evt = paywallEvents.usageLimitReached(feature, used, limit, tier as SubscriptionTier);
    trackEvent(evt.event, evt.properties);
  }, [open, feature, used, limit, tier]);

  const handleUpgrade = () => {
    const event = upsellEvents.usageNudgeUpgradeClicked(feature, 100, 'modal');
    trackEvent(event.event, event.properties);
    onOpenChange(false);
    navigate(getUpgradeUrl('usage_nudge_100'));
  };

  const handleBuyPack = () => {
    trackEvent('resource_pack_purchase_clicked', {
      feature,
      pack_type: pack.type,
      pack_name: pack.name,
      pack_price: pack.price,
      source: 'usage_gate_modal',
    });
    purchaseResourcePack(pack.type);
  };

  const handleDismiss = () => {
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) handleDismiss(); }}>
      <DialogContent className="max-w-[calc(100vw-2rem)] sm:max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader className="text-center sm:text-center">
          <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30">
            <AlertTriangle className="h-6 w-6 text-red-600 dark:text-red-400" />
          </div>
          <DialogTitle>You've reached your limit</DialogTitle>
          <DialogDescription>
            You've used all {limit} {featureName.toLowerCase()} ({used}/{limit}).
            {isPaid
              ? ' Add more instantly with a resource pack, or upgrade your plan for higher limits.'
              : ' Upgrade for higher limits \u2014 start with a 7-day free trial.'}
          </DialogDescription>
        </DialogHeader>

        <p className="text-center text-sm font-medium text-amber-700 dark:text-amber-400">
          {getPersonalizedSavings(profile)}
        </p>

        {isPaid ? (
          <div className="rounded-lg border bg-slate-50 p-4 dark:bg-slate-900">
            <p className="mb-1 text-sm font-medium text-slate-700 dark:text-slate-300">
              {pack.name} &mdash; ${pack.price}
            </p>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              {pack.adds}
            </p>
          </div>
        ) : (
          <div className="rounded-lg border bg-slate-50 p-4 dark:bg-slate-900">
            <p className="mb-2 text-sm font-medium text-slate-700 dark:text-slate-300">
              With a paid plan you get:
            </p>
            <ul className="space-y-1.5">
              {CORE_BENEFITS.map((benefit) => (
                <li
                  key={benefit}
                  className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400"
                >
                  <Check className="h-4 w-4 flex-shrink-0 text-green-500" />
                  {benefit}
                </li>
              ))}
            </ul>
          </div>
        )}

        <DialogFooter className="flex-col gap-2 sm:flex-col">
          {isPaid ? (
            <>
              <Button
                onClick={handleBuyPack}
                className="w-full"
                size="lg"
                disabled={isPurchasingResourcePack}
              >
                {isPurchasingResourcePack ? 'Processing...' : `Buy ${pack.name} — $${pack.price}`}
                {!isPurchasingResourcePack && <ShoppingCart className="ml-2 h-4 w-4" />}
              </Button>
              <Button
                onClick={handleUpgrade}
                variant="outline"
                className="w-full"
                size="lg"
              >
                Upgrade plan
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </>
          ) : (
            <Button onClick={handleUpgrade} className="w-full" size="lg">
              Start free trial
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
